import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import MatterDocumentsPanel from '../../components/MatterDocumentsPanel';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import RecommendedTopicCard from '../../components/education/RecommendedTopicCard';
import RecommendationEmptyState from '../../components/education/RecommendationEmptyState';
import AssignedLearningPanel from '../../components/education/AssignedLearningPanel';
import { buildLearningTopicPath } from '../../lib/educationContent';
import { buildTopicRecommendations } from '../../lib/learningRecommendations';

const ClientMatterDetail = () => {
  const { matterId } = useParams();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const [matter, setMatter] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [topicRecommendations, setTopicRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMatter = async () => {
      if (!profile?.advisor_firm_id || !matterId) return;

      try {
        setLoading(true);
        setError(null);

        const [matterRes, progressRes, sectionsRes, questionsRes, responsesRes] = await Promise.all([
          supabase
            .from('matters')
            .select('*, client:clients(*)')
            .eq('id', matterId)
            .eq('advisor_firm_id', profile.advisor_firm_id)
            .single(),
          supabase
            .from('matter_intake_progress')
            .select('*')
            .eq('matter_id', matterId)
            .single(),
          supabase
            .from('intake_sections')
            .select('*')
            .order('sort_order', { ascending: true }),
          supabase
            .from('intake_questions')
            .select('*')
            .order('sort_order', { ascending: true }),
          supabase
            .from('matter_intake_responses')
            .select('question_id, response_json')
            .eq('matter_id', matterId)
        ]);

        if (matterRes.error || !matterRes.data) {
          throw new Error('Plan not found or access denied.');
        }

        const clientVisibleQuestions = (questionsRes.data || []).filter((question) => question.client_visible !== false);

        setMatter(matterRes.data);
        setProgress(progressRes.data || null);
        setSections(sectionsRes.data || []);
        setQuestions(clientVisibleQuestions);

        const responseMap = {};
        responsesRes.data?.forEach(response => {
          responseMap[response.question_id] = response.response_json?.value;
        });
        setResponses(responseMap);

        const [{ data: rules }, { data: topicLinks, error: topicLinksErr }] = await Promise.all([
          supabase
            .from('education_recommendation_rules')
            .select('*, resource:education_resources(*)'),
          supabase
            .from('education_question_topics')
            .select(`
              question_key,
              context_label,
              immediate_context,
              display_priority,
              topic:education_topics(
                id,
                title,
                slug,
                content_category,
                short_summary,
                why_we_are_asking,
                plain_english_explanation,
                display_priority
              )
            `)
            .eq('is_active', true)
            .order('display_priority', { ascending: true })
        ]);

        if (topicLinksErr) throw topicLinksErr;

        if (rules) {
          const matched = [];
          const seenIds = new Set();

          rules.forEach(rule => {
            if (seenIds.has(rule.resource_id)) return;

            const question = clientVisibleQuestions.find(q => (q.question_key || q.key) === rule.question_key);
            if (!question) return;

            const value = responseMap[question.id];
            const triggered = rule.trigger_value === 'any_nonempty'
              ? value !== undefined && value !== null && value !== ''
              : value === rule.trigger_value;

            if (triggered && rule.resource) {
              matched.push({ ...rule.resource, reason: rule.recommendation_reason });
              seenIds.add(rule.resource_id);
            }
          });

          setRecommendations(matched);
        }

        setTopicRecommendations(buildTopicRecommendations({
          questions: clientVisibleQuestions,
          responses: responseMap,
          topicLinks: topicLinks || []
        }));
      } catch (err) {
        console.error('Client matter detail error:', err);
        setError(err.message || 'Unable to load plan details.');
      } finally {
        setLoading(false);
      }
    };

    fetchMatter();
  }, [matterId, profile]);

  const completion = progress?.completion_percent || 0;
  const submitted = progress?.status === 'submitted' || matter?.status === 'intake_submitted';

  const answeredCount = useMemo(() => {
    return questions.filter(q => {
      const value = responses[q.id];
      return value !== undefined && value !== null && value !== '';
    }).length;
  }, [questions, responses]);

  const getStatusLabel = () => {
    if (matter?.status === 'intake_submitted') return 'Intake Submitted';
    if (matter?.status === 'intake_in_progress') return 'Intake In Progress';
    if (matter?.status === 'completed') return 'Completed';
    return 'Plan Created';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-10 w-10 text-slate-300 mb-4" />
        <p className="text-slate-500">Loading plan details...</p>
      </div>
    );
  }

  if (error || !matter) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-8 bg-white rounded-xl border border-red-100 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900">Unable to load plan</h2>
        <p className="text-slate-600 mt-2">{error || 'Plan unavailable.'}</p>
        <button onClick={() => navigate('/dashboard')} className="mt-6 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center">
          <button onClick={() => navigate('/dashboard')} className="mr-4 p-2 rounded-full hover:bg-white transition-colors text-slate-400 hover:text-slate-600">
            <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{matter.plan_type}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Created {format(new Date(matter.created_at), 'MMM d, yyyy')} - {getStatusLabel()}
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate(submitted ? `/matters/${matter.id}/intake-summary` : `/matters/${matter.id}/intake`)}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900 transition-colors"
        >
          {submitted ? 'Review Intake' : completion > 0 ? 'Continue Intake' : 'Start Intake'}
          <SafeIcon icon={FiIcons.FiChevronRight} className="ml-2 h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Plan Status</p>
          <p className="text-lg font-bold text-slate-900 mt-2">{getStatusLabel()}</p>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Intake Progress</p>
          <p className="text-lg font-bold text-slate-900 mt-2">{completion}% Complete</p>
        </div>
        <div className="bg-white p-5 rounded-lg border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Answers Provided</p>
          <p className="text-lg font-bold text-slate-900 mt-2">{answeredCount} of {questions.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">What Happens Next</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex gap-3">
            <SafeIcon icon={FiIcons.FiClipboard} className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-slate-600">Complete your intake questionnaire so the law firm has the information needed for review.</p>
          </div>
          <div className="flex gap-3">
            <SafeIcon icon={FiIcons.FiUserCheck} className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-slate-600">Your financial advisor coordinates workflow but does not provide legal advice.</p>
          </div>
          <div className="flex gap-3">
            <SafeIcon icon={FiIcons.FiBriefcase} className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-slate-600">Legal services are provided by the participating law firm.</p>
          </div>
        </div>
      </div>

      <MatterDocumentsPanel matter={matter} mode="client" />

      <AssignedLearningPanel
        matterIds={[matter.id]}
        matters={[matter]}
        emptyText="Your advisor or attorney may assign short learning topics here for this plan."
      />

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900">Recommended Learning</h2>
        {topicRecommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topicRecommendations.slice(0, 6).map((topic) => (
              <RecommendedTopicCard
                key={topic.id}
                topic={topic}
                onOpen={(selectedTopic) => navigate(buildLearningTopicPath({
                  slug: selectedTopic.slug,
                  matterId: matter.id,
                  questionKey: selectedTopic.source_question_key
                }))}
              />
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended learning yet">
            Relevant learning topics may appear here after more intake answers are completed.
          </RecommendationEmptyState>
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900">Recommended Guidance</h2>
        {recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendations.map(resource => (
              <div key={resource.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase rounded">{resource.resource_type}</span>
                  <span className="text-xs text-slate-400">{resource.estimated_minutes} min</span>
                </div>
                <h3 className="font-bold text-slate-900">{resource.title}</h3>
                <p className="text-xs text-slate-500 mt-2 flex-1">{resource.short_description}</p>
                <button onClick={() => navigate(`/education/${resource.slug}`)} className="mt-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-700 bg-slate-50 rounded hover:bg-slate-100 transition-colors">
                  View Resource
                </button>
              </div>
            ))}
          </div>
        ) : (
          <RecommendationEmptyState title="No recommended resources yet">
            Helpful resources may appear here when they match the intake answers for this plan.
          </RecommendationEmptyState>
        )}
      </div>

      <div className="text-center pt-4 border-t border-slate-200">
        <p className="text-[10px] text-slate-400 italic">
          This platform provides technology and workflow support. Legal services are provided by the participating law firm.
        </p>
      </div>
    </div>
  );
};

export default ClientMatterDetail;
